import './ticket-form-modal.js';
import type { Cliente, Ticket, StatusTicket } from '@pgcustom/shared-types';
import type { Resultado } from '../../core/resultado.js';
import type { PgTable, PgTableColumn, PgTableRow, PgKpiCard } from '@pgcustom/design-system';
import type { SuporteApi } from './suporte.api.js';
import type { ClientesApi } from '../clientes/clientes.api.js';
import type { PermissoesSuporte } from './suporte.permissoes.js';
import { formatarDataHora } from '../pedidos/formatacao.js';
import type { ErpTicketFormModal } from './ticket-form-modal.js';

const COLUNAS: PgTableColumn[] = [
  { key: 'clienteId', label: 'Cliente' },
  { key: 'canal', label: 'Canal' },
  { key: 'assunto', label: 'Assunto' },
  { key: 'status', label: 'Status' },
  { key: 'criadoEm', label: 'Aberto em' },
];

const ROTULO_STATUS: Record<StatusTicket, string> = {
  aberto: 'Aberto',
  fechado: 'Fechado',
};

const TOM_STATUS: Record<StatusTicket, 'alerta' | 'neutro'> = {
  aberto: 'alerta',
  fechado: 'neutro',
};

/**
 * `<erp-suporte-page>` — inbox unificado de tickets (Sprint 10,
 * `ROADMAP.md`/`PRODUCT.md` 3.8). Clique num ticket `aberto` fecha
 * diretamente (ação real, não destrutiva — mesmo padrão de "marcar como
 * entregue" em `<erp-envios-page>`); ticket `fechado` é terminal, sem
 * reabertura. A lista de clientes vem de `ClientesApi` só para alimentar
 * o seletor do modal de criação — nunca é exibida como tabela própria
 * aqui.
 */
export class ErpSuportePage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _suporteApi: SuporteApi | null = null;
  private _clientesApi: ClientesApi | null = null;
  private _permissoes: PermissoesSuporte = { ler: false, criar: false, atualizar: false };

  private resultadoTickets: Resultado<Ticket[]> = { status: 'carregando' };
  private ticketsAtuais: Ticket[] = [];
  private mensagemAcao = '';
  private nomesPorClienteId = new Map<string, string>();

  private modalTicket!: ErpTicketFormModal;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set suporteApi(valor: SuporteApi) {
    this._suporteApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set clientesApi(valor: ClientesApi) {
    this._clientesApi = valor;
  }

  set permissoes(valor: PermissoesSuporte) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._suporteApi) void this.carregar();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .topo { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-5, 24px); }
        h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-xl, 1.5rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0; }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: 2px; }
        .dica { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); margin-top: var(--pg-space-3, 12px); }
        .erro-acao { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-erro, #e5484d); margin-top: var(--pg-space-3, 12px); }
        .erro-acao:empty { display: none; }
        .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-4, 16px); }
      </style>
      <div class="topo">
        <div>
          <h1>Suporte</h1>
          <div class="subtitulo">Inbox de tickets — clique num ticket aberto para fechá-lo</div>
        </div>
        ${this._permissoes.criar ? '<pg-button id="botao-novo-ticket" variant="primary">Novo ticket</pg-button>' : ''}
      </div>

      <div class="kpis">
        <pg-kpi-card id="kpi-abertos" label="Abertos" icone="suporte" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-fechados" label="Fechados" icone="suporte" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-total" label="Total" icone="suporte" estado="carregando"></pg-kpi-card>
      </div>

      <pg-card padding="compacto">
        <div id="tickets"></div>
      </pg-card>
      ${this._permissoes.atualizar ? '<p class="dica">Clique em um ticket aberto para fechá-lo.</p>' : ''}
      <p class="erro-acao">${escaparHtml(this.mensagemAcao)}</p>

      <erp-ticket-form-modal id="modal-ticket"></erp-ticket-form-modal>
    `;

    this.modalTicket = this.root.querySelector('#modal-ticket') as ErpTicketFormModal;
    if (this._suporteApi) this.modalTicket.suporteApi = this._suporteApi;
    this.modalTicket.aoSalvar = () => void this.carregar();

    this.root.querySelector('#botao-novo-ticket')?.addEventListener('pg-click', () => void this.abrirModalNovoTicket());

    this.root.querySelector('#tickets')?.addEventListener('pg-row-click', (evento) => {
      if (!this._permissoes.atualizar) return;
      const { index } = (evento as CustomEvent<{ row: PgTableRow; index: number }>).detail;
      const ticket = this.ticketsAtuais[index];
      if (ticket && ticket.status === 'aberto') void this.fechar(ticket.id);
    });

    this.atualizar();
  }

  private async abrirModalNovoTicket(): Promise<void> {
    if (!this._clientesApi) return;
    const resultado = await this._clientesApi.listar({ limit: 100 });
    const clientes: Cliente[] = resultado.status === 'ok' ? resultado.dado : [];
    this.modalTicket.abrir(clientes);
  }

  private async fechar(id: string): Promise<void> {
    if (!this._suporteApi) return;
    try {
      await this._suporteApi.fechar(id);
      this.mensagemAcao = '';
      void this.carregar();
    } catch {
      this.mensagemAcao = 'não foi possível fechar o ticket. tente novamente.';
      this.renderEsqueleto();
    }
  }

  private async carregar(): Promise<void> {
    const api = this._suporteApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultadoTickets = { status: 'sem-permissao' };
      this.atualizar();
      return;
    }

    this.resultadoTickets = { status: 'carregando' };
    this.atualizar();
    this.resultadoTickets = await api.listar();
    this.atualizar();
    if (this.resultadoTickets.status === 'ok') void this.resolverNomesClientes(this.resultadoTickets.dado);
  }

  /** Mesma técnica de `<erp-pedidos-page>.resolverNomesClientes` — cache que só cresce, nunca refaz uma chamada já feita. */
  private async resolverNomesClientes(tickets: Ticket[]): Promise<void> {
    const api = this._clientesApi;
    if (!api) return;

    const idsFaltando = [...new Set(tickets.map((t) => t.clienteId))].filter(
      (id) => !this.nomesPorClienteId.has(id)
    );
    if (idsFaltando.length === 0) return;

    const resultados = await Promise.allSettled(idsFaltando.map((id) => api.buscarPorId(id)));
    resultados.forEach((resultado, indice) => {
      if (resultado.status === 'fulfilled') {
        this.nomesPorClienteId.set(idsFaltando[indice]!, resultado.value.nome);
      }
    });

    if (this.resultadoTickets.status === 'ok' && this.resultadoTickets.dado === tickets) this.atualizar();
  }

  private atualizar(): void {
    const container = this.root.querySelector('#tickets');
    if (!container) return;

    if (this.resultadoTickets.status === 'ok') {
      this.ticketsAtuais = this.resultadoTickets.dado;
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS;
      tabela.rows = this.ticketsAtuais.map((t) => ({
        clienteId: this.nomesPorClienteId.get(t.clienteId) ?? t.clienteId.slice(0, 8) + '…',
        canal: t.canal,
        assunto: t.assunto,
        status: { texto: ROTULO_STATUS[t.status], tom: TOM_STATUS[t.status] },
        criadoEm: formatarDataHora(t.criadoEm),
      }));
      this.atualizarKpis(this.ticketsAtuais);
      return;
    }

    this.ticketsAtuais = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultadoTickets);
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    for (const id of ['kpi-abertos', 'kpi-fechados', 'kpi-total']) {
      const kpi = this.root.querySelector(`#${id}`) as PgKpiCard | null;
      if (!kpi) continue;
      if (this.resultadoTickets.status === 'vazio') {
        kpi.setAttribute('estado', 'ok');
        kpi.setAttribute('valor', '0');
      } else {
        kpi.setAttribute('estado', variant === 'carregando' ? 'carregando' : 'indisponivel');
      }
    }
  }

  private atualizarKpis(tickets: Ticket[]): void {
    const abertos = tickets.filter((t) => t.status === 'aberto').length;
    const fechados = tickets.filter((t) => t.status === 'fechado').length;
    (this.root.querySelector('#kpi-abertos') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-abertos') as PgKpiCard | null)?.setAttribute('valor', String(abertos));
    (this.root.querySelector('#kpi-fechados') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-fechados') as PgKpiCard | null)?.setAttribute('valor', String(fechados));
    (this.root.querySelector('#kpi-total') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-total') as PgKpiCard | null)?.setAttribute('valor', String(tickets.length));
  }
}

function estadoVazio(resultado: Resultado<Ticket[]>): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo: 'Suporte', descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo: 'Suporte', descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo: 'Suporte', descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhum ticket', descricao: 'tickets de suporte aparecem aqui quando abertos' };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-suporte-page', ErpSuportePage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-suporte-page': ErpSuportePage;
  }
}
